import type {
  FmFormFlowActionDescriptor,
  FmFormFlowBlockDescriptor,
  FmFormFlowItemState,
  FmFormFlowPageDescriptor,
  FmFormFlowSectionItemDescriptor,
  FmFormFlowLinearItemDescriptor,
  FmFormFlowNavigationDescriptor,
  FmFormFlowStageState,
  FmFormFlowStageStateCopy,
  FmFormFlowStageStoryDescriptor,
} from './FmFormFlowPage.types';
import type {
  FmActionConfirmationDescriptor,
  FmConfirmationAffectedEntityDescriptor,
} from './FmConfirm';

type FormFlowPrimaryActionPolicy<ActionId extends string, Reason extends string> = {
  id: ActionId;
  allowed: boolean;
  reason: Reason | null;
};

export function formFlowPrimaryAction<
  ActionId extends string,
  Reason extends string,
>(
  policy: FormFlowPrimaryActionPolicy<ActionId, Reason>,
  labels: Readonly<Record<ActionId, string>>,
  reasons: Readonly<Record<Reason, string>>,
  confirmation?: FmActionConfirmationDescriptor,
): FmFormFlowActionDescriptor {
  return {
    id: policy.id,
    label: labels[policy.id],
    variant: 'primary',
    disabled: !policy.allowed,
    disabledReason: policy.reason ? reasons[policy.reason] : undefined,
    confirmation,
  };
}

interface FmFormFlowConfirmationInput extends Omit<
  FmActionConfirmationDescriptor,
  'affectedEntities'
> {
  affectedEntities: readonly FmConfirmationAffectedEntityDescriptor[];
}

/** Seal the shared confirmation shell and reject consent without named impact. */
export function defineFormFlowConfirmation(
  input: FmFormFlowConfirmationInput,
): FmActionConfirmationDescriptor {
  if (input.affectedEntities.length === 0) {
    throw new Error('Form flow confirmation requires affected entities.');
  }
  return {
    ...input,
    affectedEntities: input.affectedEntities as readonly [
      FmConfirmationAffectedEntityDescriptor,
      ...FmConfirmationAffectedEntityDescriptor[],
    ],
  };
}

export function formFlowStageStatus(
  orderedStages: readonly string[],
  activeStage: string,
  stage: string,
): FmFormFlowLinearItemDescriptor['status'] {
  const activeIndex = orderedStages.indexOf(activeStage);
  const index = orderedStages.indexOf(stage);
  if (index < 0 || activeIndex < 0) {
    throw new Error('Form flow stage is not registered.');
  }
  if (index < activeIndex) return 'complete';
  if (index === activeIndex) return 'current';
  return 'available';
}

export function activeFormFlowStageState(
  activeStage: string,
  stage: string,
  state: FmFormFlowStageState,
): FmFormFlowStageState {
  return stage === activeStage ? state : 'ready';
}

export type FmLinearFormStagePlan<Stage extends string> = Omit<
  FmFormFlowLinearItemDescriptor,
  'id' | 'status' | 'story'
> & {
  id: Stage;
  story: Omit<
    FmFormFlowStageStoryDescriptor,
    'kind' | 'state' | 'stateCopy'
  >;
};

interface FmLinearFormNavigationInput<Stage extends string> {
  stages: readonly Stage[];
  activeStage: Stage;
  stageState: FmFormFlowStageState;
  stateCopy: FmFormFlowStageStateCopy;
  ariaLabel: string;
  invalidationMessage: string;
  items: readonly FmLinearFormStagePlan<Stage>[];
}

/** Seal the repeated linear-stage shell while routes retain their own content. */
export function defineLinearFormNavigation<Stage extends string>(
  input: FmLinearFormNavigationInput<Stage>,
): FmFormFlowNavigationDescriptor {
  return {
    kind: 'linear',
    ariaLabel: input.ariaLabel,
    activeId: input.activeStage,
    invalidationMessage: input.invalidationMessage,
    items: input.items.map(({ story, ...item }) => ({
      ...item,
      status: formFlowStageStatus(
        input.stages,
        input.activeStage,
        item.id,
      ),
      story: {
        ...story,
        kind: 'story-transition',
        state: activeFormFlowStageState(
          input.activeStage,
          item.id,
          input.stageState,
        ),
        stateCopy: input.stateCopy,
      },
    })),
  };
}

export function haveExactUniqueFormFlowIds(
  currentIds: readonly string[],
  reviewedIds: readonly string[],
): boolean {
  const current = new Set(currentIds);
  const reviewed = new Set(reviewedIds);
  return current.size === currentIds.length
    && reviewed.size === reviewedIds.length
    && current.size === reviewed.size
    && [...current].every((id) => reviewed.has(id));
}

/** The navigation item the descriptor declares active. */
export function activeItem(
  descriptor: FmFormFlowPageDescriptor,
): FmFormFlowPageDescriptor['navigation']['items'][number] | undefined {
  return descriptor.navigation.items.find((item) => item.id === descriptor.navigation.activeId);
}

/** The active item, but only on a `sections` flow. */
export function activeSectionItem(
  descriptor: FmFormFlowPageDescriptor,
): FmFormFlowSectionItemDescriptor | undefined {
  if (descriptor.navigation.kind !== 'sections') return undefined;
  return descriptor.navigation.items.find((item) => item.id === descriptor.navigation.activeId);
}

/** Section states that leave the section's own controls operable. */
export function sectionStateAllowsInteraction(state: FmFormFlowItemState | undefined): boolean {
  return state === undefined || state === 'ready' || state === 'saved';
}

/** The active item's blocks, in the order the descriptor declares. */
export function activeBlocks(
  descriptor: FmFormFlowPageDescriptor,
): readonly FmFormFlowBlockDescriptor[] {
  const item = activeItem(descriptor);
  const orderedIds = item && 'story' in item ? item.story.mobileOrder : item?.blockIds ?? [];
  const byId = new Map(descriptor.blocks.map((block) => [block.id, block]));
  return orderedIds.flatMap((id) => {
    const block = byId.get(id);
    return block ? [block] : [];
  });
}

/**
 * The block a non-ready stage's notice belongs under; `undefined` leads the
 * capability slot instead.
 */
export function stageNoticeBlockId(
  item: FmFormFlowLinearItemDescriptor | undefined,
): string | undefined {
  if (!item || item.story.state === 'ready' || item.story.state === 'loading') return undefined;
  return item.story.stateAnchor?.blockId;
}

/**
 * Actions a state surface already presents. The toolbar must not render them a
 * second time — the locked state's unlock CTA and a greyed-out twin of it in the
 * action row are the same button said twice, and one of them is dead
 * — and nothing may RELABEL them either: a resolving
 * control is not the stage's primary action and must keep its own words
 */
export function stateSurfaceActionIds(descriptor: FmFormFlowPageDescriptor): ReadonlySet<string> {
  if (descriptor.state === 'empty') return new Set([descriptor.empty.primaryActionId]);
  if (descriptor.state === 'error') return new Set([descriptor.error.retryActionId]);
  if (descriptor.state === 'locked' && descriptor.locked.unlock) {
    return new Set([descriptor.locked.unlock.unlockActionId]);
  }
  /* The stage notice owns its resolving control for exactly the same reason: a
     capacity refusal that offers "Open Settings" inside the notice and again in
     the action row is the same button said twice.
     Only while the notice is actually RENDERED, though — it is mounted solely on
     a ready descriptor, so stripping the control on a loading or locked one would
     take it off the toolbar and put it nowhere, a dead end rather than a
     duplicate. */
  if (descriptor.state !== 'ready') return new Set<string>();
  const active = descriptor.navigation.items.find((item) => item.id === descriptor.navigation.activeId);
  /* `stageNoticeBlockId` excludes `loading` as well as `ready` — a loading stage
     shows a spinner, not a notice — so the toolbar must keep the action in that
     state too, or it is presented nowhere. */
  const resolving = active
    && 'story' in active
    && active.story.state !== 'ready'
    && active.story.state !== 'loading'
    ? active.story.stateAnchor?.resolvingActionId
    : undefined;
  return resolving ? new Set([resolving]) : new Set<string>();
}

/**
 * Stage states whose controls the renderer disables.
 *
 * NOT the validator's predicate, deliberately. The paid-boundary rule asks a
 * different question — "is this previewed behind a boundary the owner has not
 * crossed" — and reusing this one would refuse a transient `loading` stage, which
 * would throw on a real measurement flow mid-refetch.
 */
export function stageAllowsControls(state: FmFormFlowStageState | undefined): boolean {
  return state !== 'loading' && state !== 'locked';
}
